// ChewHubReport — public tenant reporter for Chew Flats (5346 Chew Ave).
// One shared link at /report/chew-flats. Tenant picks unit → describes issue
// + snaps photos → AI drafts WO title/description → proposes entry windows
// → confirmation. No login. On submit: real WO created + PMR/ProCall/LaSalle
// all get emailed.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Camera, Sparkle, CheckCircle, Clock, PaperPlaneTilt, Warning, Trash, Info, ImageSquare } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ChewHubReport() {
  const [step, setStep] = useState(-1);     // -1=welcome, 0=unit, 1=describe+photos, 2=AI draft review, 3=entry windows, 4=done
  const [cfg, setCfg] = useState(null);
  // Logo variant — "official" (LaSalle U mark) or "fun" (SpongeBob co-brand).
  // Persists in localStorage so the choice sticks across sessions per device.
  const [logoVariant, setLogoVariant] = useState(() => {
    try { return localStorage.getItem("chew_hub_logo_variant") || "official"; }
    catch { return "official"; }
  });
  const toggleLogo = () => {
    setLogoVariant(v => {
      const next = v === "official" ? "fun" : "official";
      try { localStorage.setItem("chew_hub_logo_variant", next); } catch {}
      return next;
    });
  };
  const logoSrc = cfg?.brand_logo_url
    ? (cfg.brand_logo_url.startsWith("http") ? cfg.brand_logo_url : `${BASE}${cfg.brand_logo_url}`)
    : (logoVariant === "fun" ? "/lasalle-logo-fun.jpg" : "/lasalle-logo.jpeg");
  const [unit, setUnit] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [tenantContact, setTenantContact] = useState("");
  const [description, setDescription] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [urgency, setUrgency] = useState("normal");   // set by AI or by isEmergency
  const [ticketId, setTicketId] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const [photos, setPhotos] = useState([]); // [{ path, previewUrl }]
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [draft, setDraft] = useState(null); // { title, description, category, priority, priority_reason }
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateReason, setEscalateReason] = useState("");
  const [escalateTarget, setEscalateTarget] = useState("high");
  const [windows, setWindows] = useState([{ start: "", end: "", label: "Weekday morning" }]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef();
  const cameraRef = useRef();

  useEffect(() => {
    axios.get(`${BASE}/api/public/chew/hub`).then(r => setCfg(r.data)).catch(e => setError("Could not load property info"));
  }, []);

  async function createDraft() {
    if (!unit || !tenantName.trim()) { setError("Pick your unit and enter your initials (e.g. J.D.)."); return; }
    if (!description.trim()) { setError("Tell us what's going on so the tech knows what to bring."); return; }
    setError("");
    try {
      const finalUrgency = isEmergency ? "emergency" : "normal";
      setUrgency(finalUrgency);
      const r = await axios.post(`${BASE}/api/public/chew/hub/report`, {
        unit, tenant_name: tenantName, tenant_contact: tenantContact,
        description, urgency: finalUrgency, category: "other",
      });
      setTicketId(r.data.ticket_id);
      setRefCode(r.data.ref_code);
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start the request. Try again.");
    }
  }

  async function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length || !ticketId) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(
          `${BASE}/api/public/chew/hub/report/${ticketId}/photo`,
          fd, { headers: { "Content-Type": "multipart/form-data" } }
        );
        setPhotos(prev => [...prev, { path: r.data.path, previewUrl: URL.createObjectURL(f) }]);
      } catch (err) {
        setError("Photo upload failed. Please try again.");
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function runAiDraft() {
    if (!photos.length) { setError("Add at least one photo so the AI can help."); return; }
    setError("");
    setAiBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/chew/hub/report/${ticketId}/ai-draft`);
      // Snapshot the AI-decided priority so we can detect escalation later.
      setDraft({ ...r.data.draft, _ai_priority: r.data.draft.priority });
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "AI is unavailable. You can still submit — we'll type it up.");
      setDraft({
        title: description.slice(0, 80) || "Maintenance Request",
        description,
        category: "other",
        priority: isEmergency ? "urgent" : "normal",
        priority_reason: isEmergency
          ? "Flagged as Urgent — you marked this as an emergency at intake."
          : "Flagged as Normal — AI couldn't read the photo, so we're defaulting to normal.",
      });
      setStep(2);
    } finally { setAiBusy(false); }
  }

  async function saveWindows() {
    const cleaned = windows.filter(w => w.start && w.end);
    if (!cleaned.length) { setStep(3); return; } // allow skip
    try {
      await axios.post(`${BASE}/api/public/chew/hub/report/${ticketId}/entry-windows`, { windows: cleaned });
    } catch (e) { /* non-blocking */ }
  }

  async function finalSubmit() {
    setSubmitting(true); setError("");
    try {
      await saveWindows();
      const submitBody = { ...(draft || {}) };
      // AI's priority is the source of truth. If the tenant escalated (via the
      // "It's more urgent" modal) we send the reason so the API keeps the bump.
      const aiPrio = draft?._ai_priority || draft?.priority;
      if (draft?.priority && aiPrio && draft.priority !== aiPrio && (draft._override_reason || "").length >= 20) {
        submitBody.priority = draft.priority;
        submitBody.priority_override_reason = draft._override_reason;
      } else {
        submitBody.priority = aiPrio || draft?.priority || "normal";
      }
      const r = await axios.post(`${BASE}/api/public/chew/hub/report/${ticketId}/submit`, submitBody);
      setResult(r.data);
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.detail || "Submit failed. Please try again.");
    } finally { setSubmitting(false); }
  }

  const brand = cfg?.brand_color || "#00C4CC";
  // YIQ contrast → pick black vs white text so any landlord color stays readable.
  const textOnBrand = (() => {
    const h = (brand || "#000").replace("#", "");
    if (h.length < 6) return "#fff";
    const r = parseInt(h.slice(0, 2), 16),
          g = parseInt(h.slice(2, 4), 16),
          b = parseInt(h.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 165 ? "#0f172a" : "#fff";
  })();
  const accentOnBrand = textOnBrand === "#0f172a" ? "#0f172a" : "#f4c95d";

  if (!cfg) return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500">Loading Chew Flats…</div>;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="chew-hub-report">
      {/* Header */}
      <div style={{ background: brand, color: textOnBrand }} className="px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="text-[10px] font-black uppercase tracking-[2px] opacity-90">Chew Flats · Maintenance</div>
          <div className="text-2xl font-black leading-tight mt-1">{cfg.display_name}</div>
          <div className="text-xs opacity-80 mt-1">{cfg.address} · powered by Field Radar™</div>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-md mx-auto px-4 pt-4">
        <StepperBar step={step} brand={brand}/>
        {error && <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 flex gap-2" data-testid="chew-hub-error"><Warning size={16}/>{error}</div>}
      </div>

      <div className="max-w-md mx-auto px-4 pb-32 pt-4">
        {step === -1 && (
          <div className="space-y-4" data-testid="chew-hub-welcome">
            {/* Warm welcome card with LaSalle co-branding */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div style={{ background: brand, color: textOnBrand }} className="px-6 py-8 text-center">
                <div className="inline-block bg-white rounded-2xl p-2 shadow-lg relative group" data-testid="chew-hub-lasalle-logo-wrap">
                  <img src={logoSrc} alt="LaSalle University" className="w-24 h-24 rounded-xl block object-cover" data-testid="chew-hub-lasalle-logo"/>
                  <button
                    type="button"
                    onClick={toggleLogo}
                    aria-label="Switch logo"
                    title={logoVariant === "fun" ? "Switch to official LaSalle logo" : "Switch to fun Chew Flats logo"}
                    data-testid="chew-hub-logo-toggle"
                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white text-slate-900 border-2 shadow-md text-sm font-black hover:scale-110 active:scale-95 transition-transform"
                    style={{ borderColor: accentOnBrand }}>
                    ⇄
                  </button>
                </div>
                <div style={{ color: accentOnBrand }} className="mt-4 font-black text-[11px] uppercase tracking-[3px]">Welcome, Explorers</div>
                <h1 className="font-black text-2xl mt-2 leading-tight">Welcome, LaSalle Students!</h1>
                <div className="text-sm mt-2 opacity-80">Chew Flats · 5346 Chew Ave</div>
              </div>
              <div className="p-6 space-y-4">
                <div className="text-lg font-black text-slate-900 leading-snug">
                  Sorry you're having a little trouble with your place <span className="text-2xl">😉</span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  You're in the right spot. Skip the middleman — this form goes <strong>straight to maintenance</strong>. Our AI helps write it up from a quick photo, we pick a good time to come in, and you're back to your semester.
                </p>

                {/* Trust ribbon */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2.5">
                  <TrustRow emoji="⚡" text="Most requests get scheduled within 24 hours"/>
                  <TrustRow emoji="🔒" text="Private — we don't collect your full name"/>
                  <TrustRow emoji="🛠️" text="Photo → AI writes the work order → done"/>
                  <TrustRow emoji="🕒" text="You pick the entry window that fits your schedule"/>
                </div>

                <button onClick={()=>setStep(0)}
                  className="w-full rounded-xl font-black py-4 text-sm text-white shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-[#0a2461] to-[#123a9a] hover:from-[#123a9a] hover:to-[#1a4bbb] flex items-center justify-center gap-2"
                  data-testid="chew-hub-start-btn">
                  Start a maintenance request →
                </button>
                <div className="text-center text-[10px] uppercase tracking-widest font-bold text-slate-400">Chew Flats · Managed by PMR</div>
              </div>
            </div>

            {/* Emergency escape hatch — visible from second one at intake */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 flex items-start gap-2">
              <Warning weight="fill" size={16} className="mt-0.5 flex-shrink-0 text-red-500"/>
              <div>
                <strong>Real emergency?</strong> Flooding, no heat, no power, gas smell, or locked out — check the "This is an emergency" box on the next screen and we'll page maintenance right away.
              </div>
            </div>

            {/* Returning tenant — jump to student portal */}
            <a href="/student"
              className="block bg-white rounded-xl p-3 text-center text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm"
              data-testid="chew-hub-signin-link">
              Already submitted before? <span className="text-[#0a2461] underline">Sign in to see your requests →</span>
            </a>
          </div>
        )}

        {step === 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-black">Report an issue</h2>
            <p className="text-sm text-slate-600">This goes straight to maintenance — no waiting for LaSalle to forward it.</p>
            <Field label="Your unit">
              <select value={unit} onChange={e=>setUnit(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-3 bg-white" data-testid="chew-hub-unit-select">
                <option value="">Pick your unit…</option>
                {cfg.units.map(u => <option key={u.unit} value={u.unit}>{`Unit ${u.unit}`}</option>)}
              </select>
            </Field>
            <Field label="Your initials (required — helps us tell roommates apart)">
              <input value={tenantName} onChange={e=>setTenantName(e.target.value.slice(0,6))} maxLength={6} className="w-full border border-slate-300 rounded-lg px-3 py-3 uppercase font-bold tracking-wider" placeholder="e.g. J.D." data-testid="chew-hub-name-input"/>
              <div className="text-[10px] text-slate-500 mt-1">LaSalle privacy — we don't collect your full name. Your initials + unit are all we track.</div>
            </Field>
            <Field label="Email or phone (optional — kept private)">
              <input value={tenantContact} onChange={e=>setTenantContact(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-3" placeholder="you@lasalle.edu or 215-…" data-testid="chew-hub-contact-input"/>
              <div className="text-[10px] text-slate-500 mt-1">Only used to text/email you when maintenance is on the way. Field Radar keeps this locked — the office never sees the actual number or address, just "SMS on file" or "Email on file". Skip it if you'd rather check the status link yourself.</div>
            </Field>
            <Field label="What's going on?">
              <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={4} className="w-full border border-slate-300 rounded-lg px-3 py-3" placeholder="e.g. water leaking under kitchen sink since last night" data-testid="chew-hub-desc-input"/>
            </Field>
            {/* Emergency ONLY — everything else is auto-classified by AI from
                the photo + description on the next step. Prevents everyone
                from mashing "Emergency" to jump the line. */}
            <label className={`block rounded-xl border-2 p-3 cursor-pointer transition ${isEmergency ? "border-red-500 bg-red-50" : "border-slate-200 bg-white"}`} data-testid="chew-hub-emergency-toggle">
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={isEmergency} onChange={e=>setIsEmergency(e.target.checked)} className="mt-0.5 w-5 h-5 accent-red-500" data-testid="chew-hub-emergency-checkbox"/>
                <div className="flex-1">
                  <div className="font-black text-sm text-red-700 flex items-center gap-1"><Warning weight="fill" size={16}/> This is a real emergency</div>
                  <div className="text-[11px] text-slate-600 mt-1 leading-snug">
                    Check <strong>only</strong> if: flooding right now · no heat in winter · no power · no hot water · gas smell · smoke smell · locked out · active pest (roaches/mice/bedbugs) · broken exterior door.
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 italic">Everything else our AI will rate from your photo — this is just to keep the on-call tech from being paged for slow drains at 2am.</div>
                </div>
              </div>
            </label>
            <button onClick={createDraft} className="w-full mt-2 bg-slate-900 text-white rounded-xl font-black py-4 text-sm hover:bg-slate-800" data-testid="chew-hub-continue-btn">Continue → Photos</button>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-black">Add photos</h2>
              <p className="text-sm text-slate-600">A photo helps our tech bring the right tools — and our AI will draft the work order from it.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                  <img src={p.previewUrl} alt="" className="w-full h-full object-cover"/>
                </div>
              ))}
              <button type="button" onClick={()=>cameraRef.current?.click()}
                data-testid="chew-hub-photo-take"
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:bg-slate-50">
                <Camera size={22}/>
                <span className="text-[11px] font-bold">Take photo</span>
              </button>
              <button type="button" onClick={()=>fileRef.current?.click()}
                data-testid="chew-hub-photo-pick"
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:bg-slate-50">
                <ImageSquare size={22}/>
                <span className="text-[11px] font-bold">From library</span>
              </button>
              <input ref={fileRef}   type="file" accept="image/*" multiple className="hidden"
                     onChange={onPickFiles} data-testid="chew-hub-photo-input"/>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                     onChange={onPickFiles} data-testid="chew-hub-photo-camera-input"/>
            </div>
            {uploading && <div className="text-xs text-slate-500">Uploading…</div>}
            <button onClick={runAiDraft} disabled={aiBusy || !photos.length}
              className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 disabled:opacity-50 text-white rounded-xl font-black py-4 text-sm flex items-center justify-center gap-2"
              data-testid="chew-hub-ai-btn">
              <Sparkle weight="fill" size={18}/>
              {aiBusy ? "AI is looking at your photo…" : "Let AI write the work order"}
            </button>
            <button onClick={()=>{ const p = isEmergency ? "urgent" : "normal"; setDraft({ title: description.slice(0,80) || "Maintenance Request", description, category: "other", priority: p, _ai_priority: p, priority_reason: isEmergency ? "Flagged as Urgent — you marked this as an emergency at intake." : "Flagged as Normal — no photo provided, so we're defaulting to normal." }); setStep(2); }}
              className="w-full bg-slate-100 text-slate-700 rounded-xl font-bold py-3 text-xs" data-testid="chew-hub-skip-ai-btn">
              Skip — I already described it above
            </button>
          </div>
        )}

        {step === 2 && draft && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-500 flex items-center gap-1"><Sparkle weight="fill" size={12}/> AI-drafted</div>
              <h2 className="text-lg font-black mt-1">Look right? Edit if not.</h2>
              <p className="text-sm text-slate-600">This is what our maintenance tech will see.</p>
            </div>

            {/* AI-decided priority + rationale — tenant can't just click "urgent" */}
            <div className={`rounded-xl border-2 p-3 ${draft.priority === "urgent" ? "border-red-500 bg-red-50" : draft.priority === "high" ? "border-orange-500 bg-orange-50" : "border-cyan-500 bg-cyan-50"}`} data-testid="chew-hub-priority-card">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] font-black uppercase tracking-widest opacity-70">AI decided</div>
                <div className={`inline-block text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full text-white ${draft.priority === "urgent" ? "bg-red-500" : draft.priority === "high" ? "bg-orange-500" : "bg-cyan-500"}`} data-testid="chew-hub-ai-priority-pill">{draft.priority}</div>
              </div>
              <div className="text-sm mt-2 flex items-start gap-2 text-slate-700"><Info size={14} className="mt-0.5 flex-shrink-0 opacity-60"/><span>{draft.priority_reason || "Priority set by the maintenance triage assistant."}</span></div>
              {draft._ai_priority && draft.priority === draft._ai_priority && (
                <button type="button" onClick={()=>{ setEscalateTarget(draft.priority === "normal" ? "high" : "urgent"); setEscalateReason(""); setShowEscalate(true); }}
                  className="mt-2 text-[11px] font-bold underline text-slate-600 hover:text-slate-900" data-testid="chew-hub-escalate-btn">
                  Not right — it's more urgent
                </button>
              )}
              {draft._ai_priority && draft.priority !== draft._ai_priority && (
                <div className="mt-2 text-[11px] flex items-center gap-2">
                  <span className="text-slate-500">You bumped this from <strong>{draft._ai_priority}</strong> → <strong>{draft.priority}</strong>.</span>
                  <button type="button" onClick={()=>setDraft({ ...draft, priority: draft._ai_priority, _override_reason: "" })} className="underline text-slate-600 hover:text-slate-900" data-testid="chew-hub-unescalate-btn">Undo</button>
                </div>
              )}
            </div>

            <Field label="Title">
              <input value={draft.title || ""} onChange={e=>setDraft({...draft, title: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-3 font-bold" data-testid="chew-hub-draft-title"/>
            </Field>
            <Field label="Details">
              <textarea value={draft.description || ""} onChange={e=>setDraft({...draft, description: e.target.value})} rows={5} className="w-full border border-slate-300 rounded-lg px-3 py-3" data-testid="chew-hub-draft-desc"/>
            </Field>
            <Field label="Category">
              <select value={draft.category || "other"} onChange={e=>setDraft({...draft, category: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-3 bg-white" data-testid="chew-hub-draft-cat">
                {["plumbing","electrical","hvac","appliance","structural","pest","door","window","lock","other"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <button onClick={()=>setStep(3)} className="w-full bg-slate-900 text-white rounded-xl font-black py-4 text-sm" data-testid="chew-hub-to-windows-btn">Continue → Pick entry times</button>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-black flex items-center gap-2"><Clock weight="fill" size={20} className="text-cyan-500"/> When can we come in?</h2>
              <p className="text-sm text-slate-600">Give us 2–3 windows that work. We'll pick one and confirm before we come.</p>
            </div>
            {windows.map((w, i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 relative">
                <input placeholder="Label (e.g. Tue morning)" value={w.label} onChange={e=>{ const c=[...windows]; c[i].label=e.target.value; setWindows(c); }} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`chew-hub-win-label-${i}`}/>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">From
                    <input type="datetime-local" value={w.start} onChange={e=>{ const c=[...windows]; c[i].start=e.target.value; setWindows(c); }} className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid={`chew-hub-win-start-${i}`}/>
                  </label>
                  <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">To
                    <input type="datetime-local" value={w.end} onChange={e=>{ const c=[...windows]; c[i].end=e.target.value; setWindows(c); }} className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid={`chew-hub-win-end-${i}`}/>
                  </label>
                </div>
                {windows.length > 1 && (
                  <button onClick={()=>setWindows(windows.filter((_,x)=>x!==i))} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" data-testid={`chew-hub-win-remove-${i}`}><Trash size={16}/></button>
                )}
              </div>
            ))}
            {windows.length < 3 && (
              <button onClick={()=>setWindows([...windows, { start: "", end: "", label: "" }])} className="w-full border-2 border-dashed border-slate-300 rounded-xl py-3 text-sm font-bold text-slate-600" data-testid="chew-hub-add-window-btn">+ Add another window</button>
            )}
            <button onClick={finalSubmit} disabled={submitting} className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 disabled:opacity-50 text-white rounded-xl font-black py-4 text-sm flex items-center justify-center gap-2" data-testid="chew-hub-submit-btn">
              <PaperPlaneTilt weight="fill" size={18}/> {submitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        )}

        {step === 4 && result && (
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center space-y-4" data-testid="chew-hub-done">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle weight="fill" size={40} className="text-emerald-500"/></div>
            <h2 className="text-xl font-black">Request submitted</h2>
            <div className="text-sm text-slate-600">Your reference code is</div>
            <div className="font-mono text-2xl font-black tracking-widest bg-slate-100 rounded-xl py-3">{result.ref_code}</div>
            <p className="text-xs text-slate-500">We've notified maintenance ({result.notifications_sent} email{result.notifications_sent === 1 ? "" : "s"} sent). You'll get a confirmation once we pick your entry window.</p>
            {result.status_url && (
              <a href={result.status_url} className="block bg-slate-900 text-white rounded-xl font-black py-3 text-sm no-underline" data-testid="chew-hub-status-link">Check status anytime →</a>
            )}
            <a href="/student" className="block text-[11px] font-bold text-slate-500 hover:text-slate-900 underline mt-2" data-testid="chew-hub-portal-link">
              Or sign in with your email to see all your requests →
            </a>
          </div>
        )}
      </div>

      {/* Escalate priority modal — requires reason >= 20 chars + explicit confirm */}
      {showEscalate && draft && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={()=>setShowEscalate(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4" onClick={e=>e.stopPropagation()} data-testid="chew-hub-escalate-modal">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-red-500 flex items-center gap-1"><Warning weight="fill" size={12}/> Raising the priority</div>
              <h3 className="text-lg font-black mt-1">Are you sure it's more urgent?</h3>
              <p className="text-sm text-slate-600 mt-1">Our AI flagged this as <strong>{draft._ai_priority}</strong>. Escalating pages maintenance and can wake the on-call tech. Only do this for real safety / no-heat / active-flood situations.</p>
            </div>
            <div>
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Raise to</div>
              <div className="grid grid-cols-2 gap-2">
                {["high","urgent"].filter(p => {
                  const rank = { normal: 0, high: 1, urgent: 2 };
                  return rank[p] > rank[draft._ai_priority || "normal"];
                }).map(p => (
                  <button key={p} type="button" onClick={()=>setEscalateTarget(p)}
                    className={`rounded-lg p-2.5 text-xs font-black border-2 transition ${escalateTarget===p ? (p==="urgent" ? "border-red-500 bg-red-50 text-red-700" : "border-orange-500 bg-orange-50 text-orange-700") : "border-slate-200 text-slate-500"}`}
                    data-testid={`chew-hub-escalate-target-${p}`}>
                    {p.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Why is this more urgent? <span className="text-red-500 normal-case font-normal">(20 characters min)</span></div>
              <textarea value={escalateReason} onChange={e=>setEscalateReason(e.target.value)} rows={3}
                placeholder="e.g. Water is spraying out of the wall and I can't turn the valve off"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-hub-escalate-reason"/>
              <div className={`text-[10px] mt-1 font-bold ${escalateReason.trim().length >= 20 ? "text-emerald-600" : "text-slate-400"}`}>
                {escalateReason.trim().length} / 20
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={()=>setShowEscalate(false)} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm" data-testid="chew-hub-escalate-cancel">Never mind</button>
              <button disabled={escalateReason.trim().length < 20}
                onClick={()=>{ setDraft({ ...draft, priority: escalateTarget, _override_reason: escalateReason.trim() }); setShowEscalate(false); }}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-40" data-testid="chew-hub-escalate-confirm">
                Yes, page maintenance now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">{label}</div>
      {children}
    </label>
  );
}

function TrustRow({ emoji, text }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-700">
      <span className="text-base">{emoji}</span>
      <span>{text}</span>
    </div>
  );
}

function StepperBar({ step, brand }) {
  const steps = ["Start", "Unit", "Photos", "Review", "When", "Done"];
  // step -1 = Start, 0 = Unit, 1 = Photos, 2 = Review, 3 = When, 4 = Done
  const idx = step + 1;
  return (
    <div className="flex gap-1">
      {steps.map((s, i) => (
        <div key={i} className="flex-1 h-1 rounded-full" style={{ background: i <= idx ? brand : "#e2e8f0" }}/>
      ))}
    </div>
  );
}
